// hooks/modals/useUserBookingPermissions.ts
import { useMemo } from 'react';
import type { AuthUser } from '@/types/auth';
import type { AdTech } from '@/types/adtechs';
import type { ChannelInfo } from '@/types/channels';

// No isMultiCountry here. It was computed and never read, and it was the real user's
// even under chameleon — territory does not survive useEffectiveUser. Anything that
// wants the worn entity's territory must take it from /me, not from this hook.
interface UserTypes {
    isAdTechUser: boolean;
    isBroadcaster: boolean;
    isSuperUser: boolean;
    isAgencyOrCustomer: boolean;
}

interface AdTechRequirements {
    requires_job_number: boolean;
    requires_charged_cpm: boolean;
}

interface ChannelSelectOption {
    value: number;
    label: string;
}

interface ChannelLogic {
    getAvailableChannels: (selectedCountries?: string[]) => ChannelSelectOption[];
    getAutoSelectedChannel: () => ChannelSelectOption | null;
}

interface BookingDefaults {
    publisher_id: number | null;
    channel_id: ChannelSelectOption | null;
}

interface BookingValidation {
    areAdTechFieldsValid: (jobNumber: string, chargedCpm: string | number) => boolean;
}

interface BookingPermissions {
    userTypes: UserTypes;
    adtechRequirements: AdTechRequirements;
    channelLogic: ChannelLogic;
    defaults: BookingDefaults;
    validation: BookingValidation;
}

const emptyPermissions: BookingPermissions = {
    userTypes: {
        isAdTechUser: false,
        isBroadcaster: false,
        isSuperUser: false,
        isAgencyOrCustomer: false,
    },
    adtechRequirements: { requires_job_number: false, requires_charged_cpm: false },
    channelLogic: { getAvailableChannels: () => [], getAutoSelectedChannel: () => null },
    defaults: { publisher_id: null, channel_id: null },
    validation: { areAdTechFieldsValid: () => true },
};

// Stable empties: a fresh `[]` default is a new memo key on every render, and the booking form's
// set-up effect, keyed on `defaults`, then resets the form on every render, for ever
const NO_ADTECHS: AdTech[] = [];
const NO_CHANNELS: ChannelInfo[] = [];

export const useUserBookingPermissions = (
    user: AuthUser | null | undefined,
    adtechs: AdTech[] = NO_ADTECHS,
    territorialChannels: ChannelInfo[] = NO_CHANNELS
): BookingPermissions => {
    return useMemo(() => {
        if (!user) return emptyPermissions;

        const userTypes: UserTypes = {
            isAdTechUser: user.isAdTechUser ?? user.entity_type === 'ADTECH',
            isBroadcaster: user.isBroadcaster ?? user.entity_type === 'BROADCASTER',
            isSuperUser: user.isSuperUser ?? false,
            isAgencyOrCustomer: user.isSuperUser || user.isAgency || user.isCustomer || false,
        };

        const adtechRequirements: AdTechRequirements = (() => {
            if (!userTypes.isAdTechUser) return { requires_job_number: false, requires_charged_cpm: false };
            const userAdTech = adtechs.find((a) => a.id === user.entity_id);
            return {
                requires_job_number: userAdTech?.requires_job_number ?? false,
                requires_charged_cpm: userAdTech?.requires_charged_cpm ?? false,
            };
        })();

        const channelLogic: ChannelLogic = {
            getAvailableChannels: (selectedCountries: string[] = []) => {
                let channels = territorialChannels;
                if (selectedCountries.length > 0) {
                    const userTypeLower = (user.entity_type || '').toLowerCase();
                    channels = channels.filter((c) => {
                        const matchesCountry = c.country_codes?.some((code) => selectedCountries.includes(code));
                        const isOwn = c.owner_type === userTypeLower && c.owner_id === user.entity_id;
                        return matchesCountry || isOwn;
                    });
                }
                return channels.map((c) => ({ value: c.id, label: c.display_name }));
            },

            getAutoSelectedChannel: (): ChannelSelectOption | null => {
                if (userTypes.isBroadcaster && territorialChannels.length === 1) {
                    return { value: territorialChannels[0].id, label: territorialChannels[0].display_name };
                }
                if (userTypes.isAdTechUser) {
                    const userChannels = territorialChannels.filter(
                        (c) => c.owner_type === 'adtech' && c.owner_id === user.entity_id
                    );
                    if (userChannels.length === 1) {
                        return { value: userChannels[0].id, label: userChannels[0].display_name };
                    }
                }
                return null;
            },
        };

        const defaults: BookingDefaults = {
            publisher_id: userTypes.isAdTechUser ? user.entity_id : null,
            channel_id: channelLogic.getAutoSelectedChannel(),
        };

        const validation: BookingValidation = {
            areAdTechFieldsValid: (jobNumber: string, chargedCpm: string | number): boolean => {
                if (!userTypes.isAdTechUser) return true;
                let valid = true;
                if (adtechRequirements.requires_job_number) {
                    valid = valid && (jobNumber || '').trim().length > 0;
                }
                if (adtechRequirements.requires_charged_cpm) {
                    valid = valid && !isNaN(parseFloat(String(chargedCpm))) && parseFloat(String(chargedCpm)) >= 0;
                }
                return valid;
            },
        };

        return { userTypes, adtechRequirements, channelLogic, defaults, validation };
    }, [user, adtechs, territorialChannels]);
};
